/** Parffragrance quiz v5: quiz flow and recommendation scoring. */
(() => {
  'use strict';
  const Q = window.ParfQuizV5;
  if (!Q?.CatalogFinder) return;
  const { QUESTIONS, FAMILY_ALIASES } = Q;

  Q.MatchFinder = class ParfQuizMatchFinder extends Q.CatalogFinder {
    start(clearAnswers = true) {
      this.ensureCatalogLoading().catch(() => {});
      if (clearAnswers) this.answers = {};
      this.currentQuestionIndex = 0;
      this.setActiveState('question');
      this.renderQuestion();
      this.scrollToQuiz();
    }

    renderQuestion() {
      const question = QUESTIONS[this.currentQuestionIndex];
      const progress = (this.currentQuestionIndex / QUESTIONS.length) * 100;

      if (this.elements.currentQuestion) {
        this.elements.currentQuestion.textContent = String(this.currentQuestionIndex + 1);
      }
      if (this.elements.questionTitle) this.elements.questionTitle.textContent = question.title;
      if (this.elements.questionHelp) this.elements.questionHelp.textContent = question.help || '';
      if (this.elements.progressFill) this.elements.progressFill.style.width = `${progress}%`;
      this.elements.progress?.setAttribute('aria-valuenow', String(Math.round(progress)));
      if (this.elements.previous) {
        this.elements.previous.style.visibility = this.currentQuestionIndex > 0 ? 'visible' : 'hidden';
      }

      if (this.elements.options) {
        this.elements.options.innerHTML = question.options.map((option, index) => {
          const selected = this.answers[question.id]?.value === option.value;
          return `<div class="quiz-option${selected ? ' selected' : ''}" data-index="${index}" role="radio" aria-checked="${selected}" tabindex="0"><span class="quiz-option__icon" aria-hidden="true">${this.escapeHtml(option.icon || '')}</span><span class="quiz-option__content"><span class="quiz-option__text">${this.escapeHtml(option.label)}</span><span class="quiz-option__description">${this.escapeHtml(option.description || '')}</span></span></div>`;
        }).join('');
      }
    }

    choose(optionIndex) {
      const question = QUESTIONS[this.currentQuestionIndex];
      const option = question.options[optionIndex];
      if (!option) return;

      this.answers[question.id] = option;

      this.elements.options?.querySelectorAll('.quiz-option').forEach((element, index) => {
        const selected = index === optionIndex;
        element.classList.toggle('selected', selected);
        element.setAttribute('aria-checked', String(selected));
      });

      window.setTimeout(() => this.advance(), 220);
    }

    skip() {
      const question = QUESTIONS[this.currentQuestionIndex];
      delete this.answers[question.id];
      this.advance();
    }

    advance() {
      if (this.currentQuestionIndex < QUESTIONS.length - 1) {
        this.currentQuestionIndex += 1;
        this.renderQuestion();
        return;
      }
      this.calculateResults();
    }

    goBack() {
      if (this.currentQuestionIndex === 0) return;
      this.currentQuestionIndex -= 1;
      this.renderQuestion();
    }

    editAnswers() {
      this.currentQuestionIndex = 0;
      this.setActiveState('question');
      this.renderQuestion();
      this.scrollToQuiz();
    }

    async calculateResults(forceReload = false) {
      this.setActiveState('loading');

      if (this.elements.loadingText) {
        this.elements.loadingText.textContent =
          this.catalogStatus === 'ready'
            ? 'Finding the fragrances that suit you best…'
            : 'Finishing your personalised fragrance matches…';
      }

      try {
        await this.ensureCatalogLoading(forceReload);
      } catch (_error) {
        this.showCatalogueError();
        return;
      }

      window.setTimeout(() => {
        const scored = this.getEligibleCandidates()
          .map((candidate) => this.scoreCandidate(candidate))
          .sort((a, b) => b.matchScore - a.matchScore || a.title.localeCompare(b.title));

        const deduplicated = [];
        const usedKeys = new Set();

        scored.forEach((candidate) => {
          if (usedKeys.has(candidate.canonicalKey)) return;
          usedKeys.add(candidate.canonicalKey);
          deduplicated.push(candidate);
        });

        this.renderResults(deduplicated.slice(0, this.maxRecommendations));
      }, 260);
    }

    getEligibleCandidates() {
      const gender = this.answers.gender?.value;
      const intensity = this.answers.intensity?.value;
      const avoidance = this.answers.avoid?.value;

      return this.catalog.filter((candidate) => {
        if (intensity && candidate.intensity !== intensity) return false;
        if (gender === 'men' && !['men', 'unisex'].includes(candidate.gender)) return false;
        if (gender === 'women' && !['women', 'unisex'].includes(candidate.gender)) return false;
        if (gender === 'unisex' && candidate.gender !== 'unisex') return false;
        if (avoidance && avoidance !== 'none' && this.matchesAvoidance(candidate, avoidance)) return false;
        return true;
      });
    }

    matchesAvoidance(candidate, avoidance) {
      const groups = {
        sweet: ['sweet'],
        'oud-leather': ['oud', 'leather'],
        floral: ['floral'],
        'citrus-fresh': ['citrus', 'fresh'],
        musk: ['musk']
      };

      const mainScent = String(candidate.mainScent || '').toLowerCase();

      return (groups[avoidance] || []).some((family) => {
        const mainAliases = FAMILY_ALIASES[family] || [family];
        const isMainFamily = mainAliases.some((alias) => mainScent.includes(alias));
        return isMainFamily || (candidate.familyScores[family] || 0) >= 2;
      });
    }

    scoreCandidate(candidate) {
      let points = 50;
      const reasons = [];
      const preferenceWeights = this.aggregatePreferenceWeights();
      const familyContributions = [];
      const totalPreferenceWeight = Object.values(preferenceWeights)
        .reduce((sum, value) => sum + Number(value || 0), 0);
      let matchedPreferenceWeight = 0;

      Object.entries(preferenceWeights).forEach(([family, weight]) => {
        const evidence = Math.min(candidate.familyScores[family] || 0, 4);
        if (!evidence) return;
        const confidence = Math.min(1, 0.35 + (evidence * 0.2));
        const contribution = Number(weight || 0) * confidence;
        matchedPreferenceWeight += contribution;
        familyContributions.push({ family, contribution });
      });

      if (totalPreferenceWeight > 0) {
        points += (matchedPreferenceWeight / totalPreferenceWeight) * 37;
      }

      const gender = this.answers.gender?.value;
      if (gender && gender !== 'any') {
        if (candidate.gender === gender) {
          points += 7;
          reasons.push(`${this.labelForValue('gender', gender)} match`);
        } else if (candidate.gender === 'unisex') {
          points += 4;
          reasons.push('Suitable unisex option');
        }
      }

      points += 4;
      reasons.push(candidate.intensity === 'intense' ? 'Your chosen Intense option' : 'Your chosen Regular option');
      if (candidate.collection === 'private') reasons.push('Private Collection');

      familyContributions
        .sort((a, b) => b.contribution - a.contribution)
        .slice(0, 2)
        .forEach(({ family }) => reasons.push(`${this.prettyFamily(family)} profile`));

      const evidenceTotal = Object.values(candidate.familyScores)
        .reduce((sum, value) => sum + value, 0);
      if (!evidenceTotal) points -= 8;

      return {
        ...candidate,
        matchScore: Math.max(45, Math.min(98, Math.round(points))),
        matchReasons: Array.from(new Set(reasons)).slice(0, 4)
      };
    }

    aggregatePreferenceWeights() {
      const weights = {};

      ['occasion', 'climate', 'mood', 'family'].forEach((questionId) => {
        const answerWeights = this.answers[questionId]?.weights || {};
        Object.entries(answerWeights).forEach(([family, value]) => {
          weights[family] = (weights[family] || 0) + Number(value || 0);
        });
      });

      return weights;
    }

    renderResults(recommendations) {
      const selectedPreferences = ['gender', 'intensity', 'occasion', 'climate', 'mood', 'family', 'avoid']
        .map((questionId) => this.answers[questionId]?.label)
        .filter((label) => label && label !== 'No exclusions' && label !== 'No preference');

      if (this.elements.resultsFilters) {
        this.elements.resultsFilters.textContent = selectedPreferences.join(' · ') || 'Open to all styles';
      }

      if (!recommendations.length) {
        this.showNoMatches();
        return;
      }

      if (this.elements.noMatches) this.elements.noMatches.style.display = 'none';
      if (this.elements.resultsGrid) {
        this.elements.resultsGrid.style.display = 'grid';
        this.elements.resultsGrid.innerHTML = recommendations
          .map((product, index) => this.renderProductCard(product, index))
          .join('');
      }

      this.setActiveState('results');
      this.scrollToQuiz();
    }

    showNoMatches() {
      if (this.elements.resultsGrid) {
        this.elements.resultsGrid.innerHTML = '';
        this.elements.resultsGrid.style.display = 'none';
      }
      if (this.elements.noMatchesTitle) {
        this.elements.noMatchesTitle.textContent = 'We could not find an exact match';
      }
      if (this.elements.noMatchesText) {
        this.elements.noMatchesText.textContent =
          'Try changing one answer and we will look for a wider selection of fragrances.';
      }
      if (this.elements.noMatchesEdit) this.elements.noMatchesEdit.style.display = '';
      if (this.elements.noMatchesRetry) this.elements.noMatchesRetry.style.display = 'none';
      if (this.elements.noMatches) this.elements.noMatches.style.display = 'block';

      this.setActiveState('results');
      this.scrollToQuiz();
    }

    showCatalogueError() {
      if (this.elements.resultsGrid) {
        this.elements.resultsGrid.innerHTML = '';
        this.elements.resultsGrid.style.display = 'none';
      }
      if (this.elements.noMatchesTitle) {
        this.elements.noMatchesTitle.textContent = 'We could not load your matches';
      }
      if (this.elements.noMatchesText) {
        this.elements.noMatchesText.textContent =
          'Please check your connection and try again. Your answers are still saved.';
      }
      if (this.elements.noMatchesEdit) this.elements.noMatchesEdit.style.display = 'none';
      if (this.elements.noMatchesRetry) this.elements.noMatchesRetry.style.display = '';
      if (this.elements.noMatches) this.elements.noMatches.style.display = 'block';

      this.setActiveState('results');
      this.scrollToQuiz();
    }

  };
})();
